import {
  useAuthenticatedContext,
  useIsFeatureAvailable,
} from "#src/contexts/AuthenticatedContext.helpers";
import {
  clearDefaultTableConfigurations,
  loadDefaultTableConfigurations,
} from "#src/contexts/DefaultUserInterfaceConfiguration.helpers";
import { resolveUITableConfigurationsByKey } from "#src/contexts/UserInterfaceConfigurationResolution.helpers";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { ExceptionUtils } from "#src/utils/exception";
import { useQuery } from "@tanstack/react-query";
import {
  UserInterfaceConfigurationAdapter,
  type UserInterfaceConfigurationType,
} from "@validereinc/domain";
import addMinutes from "date-fns/addMinutes";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type DefaultUserInterfaceConfigurationContextType = {
  lastAppliedDate: string;
  isLoading: boolean;
  availableDefaultConfigurations: number;
  appliedDefaultConfigurations: number;
  loadDefaultConfigurations: () => Promise<number>;
  clearDefaultConfigurations: () => Promise<number>;
  clearAllConfigurations: () => void;
};

const defaultConfigurationsLastAppliedTrackingKeyName =
  "default-ui-configs-last-applied";
const defaultConfigurationsAppliedTrackingKeyName =
  "default-ui-configs-num-applied";
const staleMinutes = 30;
export const DefaultUserInterfaceConfigurationContext =
  createContext<DefaultUserInterfaceConfigurationContextType | null>(null);

/**
 * Hook into the Default User Interface Configuration Context
 * @returns context
 */
export const useDefaultUserInterfaceConfiguration = () => {
  const ctx = useContext(DefaultUserInterfaceConfigurationContext);

  if (ctx === null) {
    throw new Error(
      "Must be called within DefaultUserInterfaceConfigurationProvider."
    );
  }

  return ctx;
};

export const DefaultUserInterfaceConfigurationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    isAuthenticated,
    isLoading: isAuthenticatedContextLoading,
    v2: {
      userInfo: { user },
      companyInfo: { company },
    },
  } = useAuthenticatedContext();
  const [areUIConfigsEnabled, isLoadingAreUIConfigsEnabled] =
    useIsFeatureAvailable({
      featureFlagQuery: "core:user_interface_configurations",
      permissionQuery: "user_interface_configurations:read",
    });
  const [defaultConfigurationsLastAppliedTrackingKey] = useStorageKey(
    ["settings"],
    defaultConfigurationsLastAppliedTrackingKeyName
  );
  const [defaultConfigurationsAppliedTrackingKey] = useStorageKey(
    ["settings"],
    defaultConfigurationsAppliedTrackingKeyName
  );
  const [lastApplied, setLastApplied] = useStickyState(
    "",
    defaultConfigurationsLastAppliedTrackingKey
  );
  const [appliedDefaultConfigurations, setAppliedDefaultConfigurations] =
    useStickyState(0, defaultConfigurationsAppliedTrackingKey);

  const defaultTableUserInterfaceConfigurationsPayload: Parameters<
    typeof UserInterfaceConfigurationAdapter.getList
  >[0] = {
    sortBy: "updated_at",
    sortDirection: "desc",
    filters: {
      company_id: company?.id,
      scope: "component",
      status: "active",
      $or: [{ is_default: true, type: "table" }, { type: "table-default" }],
      ...(lastApplied ? { updated_at: { $gt: lastApplied } } : {}),
      // @ts-expect-error TODO: Update filter typing
      isAlreadyFormatted: true,
    },
  };
  const defaultTableUserInterfaceConfigurationsQuery = useQuery({
    queryKey: [
      "ui-configs",
      "default",
      defaultTableUserInterfaceConfigurationsPayload,
    ],
    queryFn: () =>
      UserInterfaceConfigurationAdapter.getList(
        defaultTableUserInterfaceConfigurationsPayload
      ),
    enabled: areUIConfigsEnabled && !!user?.id && !!company?.id,
    staleTime: 1000 * 60 * staleMinutes,
  });
  const allDefaultTableUserInterfaceConfigurationsPayload: Parameters<
    typeof UserInterfaceConfigurationAdapter.getList
  >[0] = {
    sortBy: "updated_at",
    sortDirection: "desc",
    filters: {
      company_id: company?.id,
      scope: "component",
      status: "active",
      // @ts-expect-error filter typing not great
      $or: [{ is_default: true, type: "table" }, { type: "table-default" }],
      isAlreadyFormatted: true,
    },
  };
  const allDefaultTableUserInterfaceConfigurationsQuery = useQuery({
    queryKey: [
      "ui-configs",
      "default",
      allDefaultTableUserInterfaceConfigurationsPayload,
    ],
    queryFn: () =>
      UserInterfaceConfigurationAdapter.getList(
        allDefaultTableUserInterfaceConfigurationsPayload
      ),
    enabled: areUIConfigsEnabled && !!user?.id && !!company?.id,
    staleTime: 1000 * 60 * staleMinutes,
  });

  const loadDefaultConfigurations = ({
    tableConfigurations,
    checkLastApplied = true,
  }: {
    checkLastApplied?: boolean;
    tableConfigurations?: UserInterfaceConfigurationType[];
  }) => {
    if (!user || !company) return Promise.resolve(0);
    if (
      checkLastApplied &&
      lastApplied &&
      new Date().getTime() <=
        addMinutes(new Date(lastApplied), staleMinutes).getTime()
    )
      return Promise.resolve(0);

    // every 30 minutes, if configurations exist that were updated after the
    // last application datetime, they will be fetched and applied
    setIsLoading(true);

    return Promise.all([
      loadDefaultTableConfigurations(
        user.id,
        company.id,
        tableConfigurations
      )(),
    ])
      .then((appliedCounts) => {
        const total = appliedCounts.reduce((a, b) => a + b, 0);
        setAppliedDefaultConfigurations(total);
        if (total > 0) setLastApplied(new Date().toISOString());

        return total;
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const clearDefaultConfigurations = ({
    tableConfigurations,
  }: {
    tableConfigurations?: UserInterfaceConfigurationType[];
  }) => {
    if (!user || !company) return Promise.resolve(0);

    setIsLoading(true);

    return Promise.all([
      clearDefaultTableConfigurations(
        user.id,
        company.id,
        tableConfigurations
      )(),
    ])
      .then((clearedCounts) => {
        const total = clearedCounts.reduce((a, b) => a + b, 0);

        setAppliedDefaultConfigurations(0);
        return total;
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const clearLocalStorage = () => window.localStorage.clear();

  const clearAllConfigurations = () => {
    setIsLoading(true);
    clearLocalStorage();
    setAppliedDefaultConfigurations(0);
    setIsLoading(false);
  };

  const availableDefaultConfigurations = useMemo(() => {
    const configurations =
      allDefaultTableUserInterfaceConfigurationsQuery.data?.data;

    if (!configurations?.length || !user?.id) {
      return 0;
    }

    return resolveUITableConfigurationsByKey(configurations, user.id).size;
  }, [allDefaultTableUserInterfaceConfigurationsQuery.data?.data, user?.id]);

  // mount all default table user interface configurations on load
  useEffect(() => {
    if (
      !isAuthenticated ||
      isAuthenticatedContextLoading ||
      isLoadingAreUIConfigsEnabled ||
      !areUIConfigsEnabled ||
      !defaultTableUserInterfaceConfigurationsQuery.isSuccess
    )
      return;

    loadDefaultConfigurations({
      tableConfigurations:
        defaultTableUserInterfaceConfigurationsQuery.data.data,
      checkLastApplied: true,
    })
      .then(() => {
        ExceptionUtils.registerLifecycleEvent({
          category: "app",
          type: "info",
          message: "Default User Interface Configurations loaded.",
        });
      })
      .catch((err) => {
        ExceptionUtils.reportException(err, "warning", {
          hint: "Failed to load default user interface configurations automatically.",
          sourceComponent: "DefaultUserInterfaceConfigurationContext",
        });
      });
  }, [
    isAuthenticated,
    isAuthenticatedContextLoading,
    isLoadingAreUIConfigsEnabled,
    areUIConfigsEnabled,
    defaultTableUserInterfaceConfigurationsQuery.data,
  ]);

  return (
    <DefaultUserInterfaceConfigurationContext.Provider
      value={{
        lastAppliedDate: lastApplied,
        appliedDefaultConfigurations,
        availableDefaultConfigurations,
        isLoading: isLoading || isAuthenticatedContextLoading,
        loadDefaultConfigurations: async () => {
          const newData =
            await allDefaultTableUserInterfaceConfigurationsQuery.refetch();
          await defaultTableUserInterfaceConfigurationsQuery.refetch();

          return loadDefaultConfigurations({
            tableConfigurations: newData.data?.data,
            checkLastApplied: false,
          });
        },
        clearDefaultConfigurations: () =>
          clearDefaultConfigurations({
            tableConfigurations:
              allDefaultTableUserInterfaceConfigurationsQuery.data?.data,
          }),
        clearAllConfigurations: clearAllConfigurations,
      }}
    >
      {children}
    </DefaultUserInterfaceConfigurationContext.Provider>
  );
};
